import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, FormikValues } from 'formik';
import { isEmpty } from 'lodash';
import * as yup from 'yup';

import Header from 'components/AppBar';
import SubmitButton from 'components/Buttons/SubmitButton';
import FormikSubmitErrorEffect from 'components/FormikSubmitErrorEffect';
import Input from 'components/Input';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Section from 'components/Section';
import NationalitySelector from 'components/Selects/OnboardingSelect';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { Company } from 'models/company';
import { NationalityCountry } from 'models/country';
import { saveCompany } from 'services/companyService';

import { Container, Content, Wrapper, FormContainer, Line, Field, ButtonContainer } from './styles';

interface ICompanyBillingForm {
  country: string;
  name: string;
  document: string;
  emailBilling: string;
  contactFullname: string;
  contactPhoneNumber: string;
  contactEmail: string;
}

interface Props {
  isLoading: boolean;
  company?: Company | null;
  countries: NationalityCountry[];
}

const CompanyBillingForm = ({ isLoading, company, countries }: Props) => {
  const toast = useToast();
  const history = useHistory();
  const { t } = useStoreTranslation();

  const [loading, setLoading] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const handleSubmit = async (values: ICompanyBillingForm) => {
    setLoading(true);

    const { contactFullname } = values;
    const [firstName, ...lastName] = contactFullname.trim().split(' ');

    if (company) {
      const updatedCompany = company;
      updatedCompany.name = values.name;
      updatedCompany.countryId = values.country;
      updatedCompany.billingInformation = {
        name: (firstName || '').trim(),
        // @ts-ignore
        last_name: (lastName || '').join(' ').trim(),
        phone_number: values.contactPhoneNumber,
        email: values.contactEmail.toLowerCase(),
        billing_email: values.emailBilling.toLowerCase(),
      };

      if (!isEmpty(updatedCompany)) {
        try {
          await saveCompany(updatedCompany);
        } catch (error) {
          toast.add({
            type: 'danger',
            // @ts-ignore
            message: error.message,
          });
          setLoading(false);
        }
      }
      if (!isEdit || !updatedCompany.address) {
        history.push('/corporate/companyBillingMapEdit', history.location.state);
        return;
      }
    }
    history.push('/corporate/companyBilling', history.location.state);
  };

  const validationSchema = yup.object().shape({
    name: yup.string().trim().max(100).required(),
    document: yup.string().trim().max(100).required(),
    emailBilling: yup.string().email().required(),
    contactFullname: yup.string().trim().max(100).required(),
    contactPhoneNumber: yup.string().phone().required(),
    contactEmail: yup.string().email().required(),
  });

  const companyName = company?.name || '';
  const name = company?.billingInformation?.name || '';
  const last_name = company?.billingInformation?.last_name || '';
  const emailBilling = company?.billingInformation?.billing_email || '';
  const contactPhoneNumber = company?.billingInformation?.phone_number || '';
  const contactEmail = company?.billingInformation?.email || '';
  const country = company?.countryId || '';
  const contactFullname = name.length > 0 ? `${name}${last_name.length > 0 ? ` ${last_name}` : ''}` : '';

  const initValues = {
    name: companyName,
    document: company?.document || '',
    emailBilling: emailBilling,
    contactFullname: contactFullname,
    contactPhoneNumber: contactPhoneNumber,
    contactEmail: contactEmail,
    country: country,
  };

  useEffect(() => {
    setIsEdit(!!company?.address);
  }, [company]);

  const handleBack = () => {
    if (company && (isEmpty(company?.billingInformation) || isEmpty(company?.address))) {
      history.push('/corporate/dashboard');
      return;
    }
    history.push('/corporate/companyBilling');
  };

  return (
    <Wrapper className="target">
      {isLoading ? (
        <Loader fullScreen />
      ) : (
        <Container>
          <Desktop>
            <ScreenHeader title={t('billing.profile.title')} />
          </Desktop>
          <Mobile>
            <Header goBack={handleBack}>{t('billing.profile.title')}</Header>
          </Mobile>

          <Content>
            <Formik
              initialStatus={{ rightCheckIfIsValid: true }}
              initialValues={initValues}
              validationSchema={validationSchema}
              onSubmit={(value) => handleSubmit(value as ICompanyBillingForm)}
              isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
            >
              {({ dirty, isSubmitting }: FormikValues) => (
                <>
                  <FormikSubmitErrorEffect />

                  <FormContainer>
                    <Section title={t('billing.detail.title')}>
                      <Field name="name" label={t('billing.detail.name')}>
                        <Input id="name" placeholder={t('billing.form.name')} />
                      </Field>
                      <Field
                        name="country"
                        labelKey={t('billing.detail.country.label')}
                        //simpleTooltip
                        //simpleTooltipText={t('billing.detail.country.tooltip')}
                      >
                        <NationalitySelector
                          type="country"
                          options={countries}
                          title={t('billing.form.country')}                          
                        />
                      </Field>
                      <Field
                        name="document"
                        label={t('billing.detail.document.label')}
                        simpleTooltip
                        simpleTooltipText={t('billing.detail.document.tooltip')}
                      >
                        <Input id="document" disabled />
                      </Field>
                      <Field name="emailBilling" label={t('billing.detail.email')}>
                        <Input id="emailBilling" placeholder={t('billing.form.email')} />
                      </Field>
                    </Section>

                    <Line />

                    <Section title={t('billing.contact.title')}>
                      <Field name="contactFullname" label={t('billing.contact.name')}>
                        <Input id="contactFullname" placeholder={t('billing.form.contact.name')} />
                      </Field>
                      <Field name="contactPhoneNumber" label={t('personalDataForm.phoneNumber')}>
                        <Input type="text" inputMode="tel" placeholder={t('personalDataForm.telNumberPlaceHolder')} />
                      </Field>
                      <Field name="contactEmail" label={t('billing.contact.email')}>
                        <Input id="contactEmail" placeholder={t('billing.form.contact.email')} />
                      </Field>
                    </Section>

                    <ButtonContainer>
                      <SubmitButton isEdit={isEdit} dirty={dirty} loading={loading || isSubmitting}>
                        {isEdit ? t('billing.profile.buttonSave') : t('billing.profile.buttonContinue')}
                      </SubmitButton>
                    </ButtonContainer>
                  </FormContainer>
                </>
              )}
            </Formik>
          </Content>
        </Container>
      )}
    </Wrapper>
  );
};

export default CompanyBillingForm;
